import { SponsoredArticle } from "api/requests/getSponsoredArticles";
import { Action, Category, Label } from "constants/events";
import { Brand, ProductCategory, ProductsByCategory } from "custom-types/Brand";
import { MenuItem } from "custom-types/MenuItem";
import { useEventTracker } from "hooks/useEventTracker";

import AboutBrand from "components/brands/AboutBrand";
import BrandAdditionalInfo from "components/brands/BrandAdditionalInfo";
import BrandCoinCarousel from "components/brands/BrandCoinCarousel";
import BrandLayout from "components/brands/BrandLayoutWrapper";
import BrandProductCategories from "components/brands/BrandProductCategories";
import BrandProductCoins from "components/brands/BrandProductCoins";
import BrandProductsNearYou from "components/brands/BrandProductsNearYou";
import ProductSpotlight from "components/brands/ProductSpotlight";
import { SponsoredArticlesCarousel } from "components/products/SponsoredArticlesCarousel";

const BrandPage = ({
  brand,
  isFollowing,
  productsForCategoryCarousels,
  sortedCategories,
  sponsoredArticles,
  nearbyBrands,
  nearbyProducts,
  newBrands,
  allBrands,
}: {
  brand: Brand;
  isFollowing: boolean;
  productsForCategoryCarousels: ProductsByCategory;
  sortedCategories: ProductCategory[];
  sponsoredArticles?: SponsoredArticle[];
  nearbyBrands?: Brand[];
  nearbyProducts: MenuItem[];
  newBrands?: Brand[];
  allBrands?: Brand[];
}) => {
  const { publishEvent } = useEventTracker();
  const paidTier = brand.brandFeatureTier === "paid";
  const showAdditionalInfo =
    (brand.brandLicenses && brand.brandLicenses.length > 0) ||
    (paidTier && brand.informationWebsite);

  return (
    <BrandLayout brand={brand} isFollowing={isFollowing}>
      {paidTier && (
        <div className="flex flex-col gap-xl">
          <AboutBrand
            brandId={brand.id}
            brandDescription={brand.description}
            brandSpotlightVideoUrl={brand.spotlightVideoEmbedUrl}
          />
          {brand.featuredProducts && (
            <ProductSpotlight
              brandId={brand.id}
              brandName={brand.name}
              brandSlug={brand.slug}
              featuredProducts={brand.featuredProducts}
            />
          )}
        </div>
      )}

      {!paidTier && (
        <>
          {nearbyBrands && nearbyBrands.length > 0 && (
            <BrandCoinCarousel
              title="Shop these nearby brands"
              brands={nearbyBrands}
              eventCategory={Category.brandInfo}
              eventLabel="top brands"
              disableLazyLoad
            />
          )}
          {newBrands && newBrands.length > 0 && (
            <div className="bg-leafly-white full-bleed-container py-xl">
              <div className="container">
                <BrandCoinCarousel
                  title="Discover new brands"
                  brands={newBrands}
                  eventCategory={Category.brandInfo}
                  eventLabel="new brands"
                />
              </div>
            </div>
          )}
          {allBrands && allBrands.length > 0 && (
            <BrandCoinCarousel
              title="Shop all brands"
              brands={allBrands}
              eventCategory={Category.brandInfo}
              eventLabel="all brands"
            />
          )}
        </>
      )}

      {nearbyProducts && nearbyProducts.length > 0 && (
        <BrandProductsNearYou
          brandName={brand.name}
          brandId={brand.id}
          products={nearbyProducts}
        />
      )}
      <BrandProductCoins
        sortedCategories={sortedCategories}
        brandSlug={brand.slug}
        brandId={brand.id}
        featureTier={brand.brandFeatureTier}
      />
      {sortedCategories?.length > 0 && (
        <BrandProductCategories
          brandSlug={brand.slug}
          brandId={brand.id}
          featureTier={brand.brandFeatureTier}
          sortedCategories={sortedCategories}
          productsByCategory={productsForCategoryCarousels}
        />
      )}
      {paidTier && (
        <SponsoredArticlesCarousel
          sponsorId={brand.id}
          sponsorType="brand"
          title="Featured articles"
          className="m-0"
          showSeeMoreCard={false}
          initialArticles={sponsoredArticles}
          trackImpression={{
            category: Category.brandPaid,
            customDimensions: {
              brandId: brand.id,
            },
            label: Label.featuredArticle,
          }}
          onCardClick={(articleId?: number) =>
            publishEvent({
              action: Action.click,
              articleId,
              brandId: brand.id,
              category: Category.brandPaid,
              label: Label.featuredArticle,
            })
          }
        />
      )}
      {showAdditionalInfo && (
        <BrandAdditionalInfo
          brandId={brand.id}
          brandLicenses={brand.brandLicenses}
          informationWebsite={brand.informationWebsite}
          paidTier={paidTier}
        />
      )}
    </BrandLayout>
  );
};

export default BrandPage;
